/**
 * GitLab Job Node 组件
 * 用于在拓扑图中渲染单个 Job 节点
 */

import { useState, useRef, useMemo, useContext } from 'react';
import {
  Node,
  RunStatus,
  TaskNode,
  useHover,
  WithSelectionProps,
  WithContextMenuProps,
} from '@patternfly/react-topology';
import { Tooltip } from '@patternfly/react-core';
import { GitLabJobsContext } from './GitLabPipelineVisualization';

type GitLabJobNodeProps = {
  element: Node;
} & WithContextMenuProps &
  WithSelectionProps;

// 将 GitLab 状态映射到 PatternFly RunStatus
const getRunStatus = (status: string): RunStatus => {
  switch (status?.toLowerCase()) {
    case 'success':
      return RunStatus.Succeeded;
    case 'failed':
      return RunStatus.Failed;
    case 'running':
      return RunStatus.Running;
    case 'pending':
      return RunStatus.Pending;
    case 'manual':
      return RunStatus.Idle; // manual 使用 Idle 状态，不显示等待图标
    case 'created':
      return RunStatus.Idle;
    case 'canceled':
    case 'skipped':
      return RunStatus.Cancelled;
    default:
      return RunStatus.Pending;
  }
};

// 获取状态显示文本
const getStatusText = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'manual':
      return '手动操作';
    case 'success':
      return '成功';
    case 'failed':
      return '失败';
    case 'running':
      return '运行中';
    case 'pending':
      return '等待中';
    case 'canceled':
      return '已取消';
    case 'skipped':
      return '已跳过';
    default:
      return status;
  }
};

export const GitLabJobNode = ({ element, ...rest }: GitLabJobNodeProps) => {
  const data = element.getData();
  const triggerRef = useRef<SVGGElement | null>(null);
  const [hover, hoverRef] = useHover();
  const { openLogDialog } = useContext(GitLabJobsContext);

  const job = data.job;
  const status = getRunStatus(job?.status || 'pending');
  
  // 为 manual 状态添加特殊标识
  const isManual = job?.status?.toLowerCase() === 'manual';

  const passedData = useMemo(() => {
    const newData = { ...data };
    Object.keys(newData).forEach(key => {
      if (newData[key] === undefined) {
        delete newData[key];
      }
    });
    
    return newData;
  }, [data]);

  const taskNode = (
    <g ref={hoverRef}>
      <TaskNode
        element={element}
        {...rest}
        {...passedData}
        scaleNode={hover && !rest.selected}
        status={status}
        hideDetailsAtMedium
        hiddenDetailsShownStatuses={[RunStatus.Succeeded]}
        showStatusState
        badge={isManual ? '⚙' : (job?.duration ? `${Math.round(job.duration)}s` : undefined)}
      />
    </g>
  );

  // 添加 Tooltip 显示详细信息
  const tooltipContent = job ? (
    <div>
      <div><strong>{job.name}</strong></div>
      <div>{getStatusText(job.status)}</div>
      <div>Stage: {job.stage}</div>
      {job.duration && <div>持续时间: {Math.round(job.duration)}s</div>}
    </div>
  ) : null;

  return (
    <g
      ref={triggerRef as any}
      onClick={() => {
        // 点击节点打开日志对话框 - 复用 Tekton 的方式
        if (job?.id) {
          openLogDialog(job.id);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {tooltipContent ? (
        <Tooltip content={tooltipContent} triggerRef={triggerRef}>
          {taskNode}
        </Tooltip>
      ) : (
        taskNode
      )}
    </g>
  );
};

export default GitLabJobNode;

